package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DayOfWeek
import kotlinx.browser.document
import org.w3c.dom.HTMLElement

@Composable
fun DateScroller(
    dates: List<LocalDate>,
    selectedDate: LocalDate,
    today: LocalDate,
    onDateSelected: (LocalDate) -> Unit,
    onOpenPicker: () -> Unit,
    initialScrollDate: LocalDate? = null,
    onLoadPrevious: () -> Unit = {},
    onLoadMore: () -> Unit = {}
) {
    val scrollerClass = "fg-date-scroller"
    val todayIndex = dates.indexOf(today)
    val selectedDateState by rememberUpdatedState(selectedDate)
    var previousFirstDate by remember { mutableStateOf(dates.firstOrNull()) }
    var previousSize by remember { mutableStateOf(dates.size) }
    var initialScrollDone by remember { mutableStateOf(initialScrollDate == null) }
    var loadingPrevious by remember { mutableStateOf(false) }
    var loadingMore by remember { mutableStateOf(false) }

    // Scroll detection for infinite load
    DisposableEffect(dates) {
        val scroller = document.querySelector(".$scrollerClass") as? HTMLElement ?: return@DisposableEffect onDispose { }
        val listener = { _: dynamic ->
            val el = scroller
            val threshold = 200
            val atStart = el.scrollLeft <= threshold
            if (el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold) {
                if (!loadingMore) {
                    loadingMore = true
                    onLoadMore()
                }
            } else {
                loadingMore = false
            }
            if (atStart && selectedDateState != today) {
                if (!loadingPrevious) {
                    loadingPrevious = true
                    onLoadPrevious()
                }
            } else {
                loadingPrevious = false
            }
        }
        scroller.addEventListener("scroll", listener)
        onDispose { scroller.removeEventListener("scroll", listener) }
    }

    LaunchedEffect(initialScrollDate, dates) {
        val targetDate = initialScrollDate ?: return@LaunchedEffect
        if (initialScrollDone) return@LaunchedEffect
        val index = dates.indexOf(targetDate)
        if (index >= 0) {
            repeat(8) {
                val card = document.getElementById("date-card-$index") as? HTMLElement
                if (card != null) {
                    card.scrollIntoView(js("{ behavior: 'auto', inline: 'center', block: 'nearest' }"))
                    initialScrollDone = true
                    previousFirstDate = dates.firstOrNull()
                    previousSize = dates.size
                    return@LaunchedEffect
                }
                kotlinx.coroutines.delay(16)
            }
        }
    }

    LaunchedEffect(dates) {
        val scroller = document.querySelector(".$scrollerClass") as? HTMLElement ?: return@LaunchedEffect
        val previousFirst = previousFirstDate
        val currentFirst = dates.firstOrNull()
        if (previousFirst != null && currentFirst != null && currentFirst.toString() < previousFirst.toString()) {
            val addedDays = dates.size - previousSize
            if (addedDays > 0) {
                scroller.scrollLeft += addedDays * 60
            }
        }
        previousFirstDate = currentFirst
        previousSize = dates.size
    }

    // Scroll to selected date when it changes
    LaunchedEffect(selectedDate, dates) {
        if (!initialScrollDone) return@LaunchedEffect
        if (selectedDate == today) {
            repeat(8) {
                val scroller = document.querySelector(".$scrollerClass") as? HTMLElement
                if (scroller != null) {
                    scroller.scrollTo(js("{ left: 0, behavior: 'smooth' }"))
                    return@LaunchedEffect
                }
                kotlinx.coroutines.delay(20)
            }
            return@LaunchedEffect
        }
        val index = dates.indexOf(selectedDate)
        if (index >= 0) {
            repeat(10) {
                val card = document.getElementById("date-card-$index") as? HTMLElement
                if (card != null) {
                    card.scrollIntoView(js("{ behavior: 'smooth', inline: 'center', block: 'nearest' }"))
                    return@LaunchedEffect
                }
                kotlinx.coroutines.delay(20)
            }
        }
    }

    Nav({
        classes(scrollerClass)
        style {
            position(Position.Sticky)
            top(56.px)
            property("z-index", "95")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            overflowX("auto")
            width(100.percent)
            gap(8.px)
            padding(12.px, 0.px)
            backgroundColor(Color("var(--bg)"))
            property("border-bottom", "1px solid var(--border)")
            property("box-shadow", "0 2px 4px rgba(22, 24, 29, 0.02)")
            property("scrollbar-width", "none")
            property("-ms-overflow-style", "none")
            property("transition", "background-color 0.8s ease")
        }
    }) {
        // TODAY CARD — sticky left, always visible
        if (todayIndex >= 0) {
            Div({
                style {
                    position(Position.Sticky)
                    left(0.px)
                    property("z-index", "1")
                    flexShrink(0)
                    backgroundColor(Color("var(--bg)"))
                    paddingLeft(16.px)
                    property("box-shadow", "6px 0 12px -4px var(--bg)")
                }
            }) {
                DateCard(
                    date = today,
                    isSelected = today == selectedDate,
                    isToday = true,
                    onClick = {
                        val scroller = document.querySelector(".$scrollerClass") as? HTMLElement
                        scroller?.scrollTo(js("{ left: 0, behavior: 'smooth' }"))
                        onDateSelected(today)
                    },
                    elementId = "date-card-today"
                )
            }
        }

        // ALL OTHER DATES — scrollable
        dates.forEachIndexed { index, date ->
            if (date != today) {
                DateCard(
                    date = date,
                    isSelected = date == selectedDate,
                    isToday = false,
                    onClick = { onDateSelected(date) },
                    elementId = "date-card-$index"
                )
            }
        }

        // CALENDAR ICON — sticky right, always visible
        Div({
            id("date-card-calendar")
            style {
                position(Position.Sticky)
                right(0.px)
                property("z-index", "1")
                flexShrink(0)
                backgroundColor(Color("var(--bg)"))
                paddingRight(16.px)
                property("box-shadow", "-6px 0 12px -4px var(--bg)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    minWidth(52.px); height(58.px); borderRadius(12.px); cursor("pointer")
                    backgroundColor(Color("var(--surface)"))
                    border(1.px, LineStyle.Solid, Color("var(--border)"))
                    property("box-shadow", "0 2px 4px rgba(22, 24, 29, 0.04)")
                    property("transition", "all 0.2s ease-in-out")
                }
                onClick { onOpenPicker() }
            }) {
                LucideIcon(
                    name = Icons.CALENDAR,
                    size = 20,
                    color = "var(--muted)"
                )
            }
        }
    }
}

@Composable
private fun DateCard(
    date: LocalDate,
    isSelected: Boolean,
    isToday: Boolean,
    onClick: () -> Unit,
    elementId: String? = null
) {
    val strings = app.footyguide.core.locale.LocalStrings.current
    val weekday = getShortWeekday(date.dayOfWeek, strings)
    val day = date.day.toString()

    Div({
        classes("fg-pressable")
        if (elementId != null) id(elementId)
        onClick { onClick() }
        style {
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            minWidth(52.px); height(58.px); borderRadius(12.px); cursor("pointer"); flexShrink(0)
            position(Position.Relative)

            if (isSelected) {
                property("background-color", "var(--accent-fill, #16181D) !important")
                property("border", "1px solid var(--accent-fill, #16181D) !important")
                property("box-shadow", "0 4px 14px rgba(0, 0, 0, 0.18)")
                property("transform", "scale(1.02)")
            } else {
                backgroundColor(Color("var(--surface)"))
                border(1.px, LineStyle.Solid, Color("var(--border)"))
                property("box-shadow", "0 2px 4px rgba(22, 24, 29, 0.03)")
            }

            property("transition", "transform 140ms cubic-bezier(0.2, 0, 0, 1), background-color 160ms ease, box-shadow 160ms ease")
        }
    }) {
        Span({
            style {
                fontSize(10.px); fontWeight(700); property("text-transform", "uppercase")
                fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                letterSpacing(0.8.px)
                marginBottom(2.px)
                property("color", if (isSelected) "var(--accent-on-fill, #FFFFFF) !important" else "var(--muted)")
                if (isSelected) property("opacity", "0.88")
            }
        }) {
            Text(weekday)
        }

        Span({
            style {
                fontSize(18.px); fontWeight(800)
                fontFamily("Sora, sans-serif")
                property("font-variant-numeric", "tabular-nums")
                letterSpacing((-0.5).px)
                lineHeight("1em")
                property("color", if (isSelected) "var(--accent-on-fill, #FFFFFF) !important" else "#16181D !important")
            }
        }) {
            Text(day)
        }

        // TODAY INDICATOR (Small Dot at bottom)
        if (isToday) {
            Div({
                style {
                    position(Position.Absolute)
                    bottom(6.px)
                    width(4.px)
                    height(4.px)
                    borderRadius(50.percent)
                    property("background-color", if (isSelected) "var(--accent-on-fill, #FFFFFF) !important" else "var(--accent-fill, #0B6E4F) !important")
                }
            })
        }
    }
}

private fun getShortWeekday(
    dayOfWeek: DayOfWeek,
    strings: app.footyguide.core.locale.AppStrings? = null,
): String {
    val idx = when (dayOfWeek) {
        DayOfWeek.MONDAY -> 0
        DayOfWeek.TUESDAY -> 1
        DayOfWeek.WEDNESDAY -> 2
        DayOfWeek.THURSDAY -> 3
        DayOfWeek.FRIDAY -> 4
        DayOfWeek.SATURDAY -> 5
        DayOfWeek.SUNDAY -> 6
        else -> 0
    }
    val custom = strings?.weekdaysShort
    if (custom != null && idx in custom.indices) return custom[idx]
    return when (dayOfWeek) {
        DayOfWeek.MONDAY -> "SEG"
        DayOfWeek.TUESDAY -> "TER"
        DayOfWeek.WEDNESDAY -> "QUA"
        DayOfWeek.THURSDAY -> "QUI"
        DayOfWeek.FRIDAY -> "SEX"
        DayOfWeek.SATURDAY -> "SAB"
        DayOfWeek.SUNDAY -> "DOM"
        else -> ""
    }
}
